// Financial math helpers

export const num = (v, fallback = 0) => {
  const n = typeof v === "string" ? parseFloat(v.replace(/,/g, "")) : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const inr = (n, opts = {}) => {
  if (!Number.isFinite(n)) return "—";
  const { max = 0 } = opts;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: max,
    minimumFractionDigits: 0,
  }).format(n);
};

export const inrCompact = (n) => {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return inr(n);
};

/**
 * Standard EMI formula. principal in ₹, annualRate in %, tenureMonths in months
 * EMI = P * r * (1+r)^n / ((1+r)^n - 1)
 */
export function calcEMI(principal, annualRate, tenureMonths) {
  const p = num(principal);
  const r = num(annualRate) / 12 / 100;
  const n = num(tenureMonths);
  if (p <= 0 || n <= 0) return { emi: 0, totalInterest: 0, totalPayable: 0 };
  if (r === 0) {
    const emi = p / n;
    return { emi, totalInterest: 0, totalPayable: p };
  }
  const pow = Math.pow(1 + r, n);
  const emi = (p * r * pow) / (pow - 1);
  const totalPayable = emi * n;
  const totalInterest = totalPayable - p;
  return { emi, totalInterest, totalPayable };
}

/**
 * Amortization schedule — returns array of { month, emi, principal, interest, balance }
 */
export function amortization(principal, annualRate, tenureMonths) {
  const rows = [];
  const p = num(principal);
  const r = num(annualRate) / 12 / 100;
  const n = num(tenureMonths);
  if (p <= 0 || n <= 0) return rows;
  const { emi } = calcEMI(p, annualRate, n);
  let bal = p;
  for (let m = 1; m <= n; m++) {
    const interest = bal * r;
    const principalPaid = emi - interest;
    bal = Math.max(0, bal - principalPaid);
    rows.push({ month: m, emi, principal: principalPaid, interest, balance: bal });
  }
  return rows;
}

/**
 * Eligibility — using FOIR (Fixed Obligation to Income Ratio) methodology.
 * Assumes bank allows ~55% of net monthly income minus existing EMI as
 * disposable EMI. Then reverse-solve for principal for given rate & tenure.
 */
export function calcEligibility({ monthlyIncome, existingEmi, employmentType, annualRate, tenureMonths }) {
  const income = num(monthlyIncome);
  const existing = num(existingEmi);
  const foirMap = { Salaried: 0.55, "Self-employed Professional": 0.6, "Self-employed Business": 0.5, Professional: 0.6 };
  const foir = foirMap[employmentType] ?? 0.5;
  const disposable = Math.max(0, income * foir - existing);
  const r = num(annualRate) / 12 / 100;
  const n = num(tenureMonths);
  if (disposable <= 0 || n <= 0) return { eligibleAmount: 0, disposableEmi: disposable, foir };
  if (r === 0) return { eligibleAmount: disposable * n, disposableEmi: disposable, foir };
  const pow = Math.pow(1 + r, n);
  const principal = (disposable * (pow - 1)) / (r * pow);
  return { eligibleAmount: principal, disposableEmi: disposable, foir };
}

/**
 * Affordability — how much loan you can afford given income - expenses - existing EMI
 * Uses conservative FOIR of 40%, then adds down payment to arrive at recommended.
 */
export function calcAffordability({ income, expenses, existingEmi, downPayment, annualRate = 9, tenureMonths = 240 }) {
  const netAvailable = num(income) - num(expenses) - num(existingEmi);
  const foirCap = num(income) * 0.4 - num(existingEmi);
  const affordableEmi = Math.max(0, Math.min(netAvailable, foirCap));
  const r = num(annualRate) / 12 / 100;
  const n = num(tenureMonths);
  let loan = 0;
  if (affordableEmi > 0 && n > 0) {
    if (r === 0) loan = affordableEmi * n;
    else {
      const pow = Math.pow(1 + r, n);
      loan = (affordableEmi * (pow - 1)) / (r * pow);
    }
  }
  const recommendedLoan = loan + num(downPayment);
  const dti = num(income) > 0 ? ((num(existingEmi) + affordableEmi) / num(income)) * 100 : 0;
  return { recommendedLoan, affordableEmi, dti };
}

/**
 * Balance transfer savings
 */
export function calcBalanceTransfer({ outstanding, oldRate, newRate, remainingMonths, processingFee }) {
  const p = num(outstanding);
  const n = num(remainingMonths);
  if (p <= 0 || n <= 0) return null;
  const oldEmi = calcEMI(p, oldRate, n).emi;
  const newEmi = calcEMI(p, newRate, n).emi;
  const monthlySavings = oldEmi - newEmi;
  const oldTotalInterest = oldEmi * n - p;
  const newTotalInterest = newEmi * n - p;
  const interestSaved = oldTotalInterest - newTotalInterest;
  const fee = num(processingFee);
  const totalSavings = interestSaved - fee;
  const breakEvenMonths = monthlySavings > 0 ? Math.ceil(fee / monthlySavings) : Infinity;
  return { oldEmi, newEmi, monthlySavings, interestSaved, totalSavings, breakEvenMonths };
}
